import { RefObject, useCallback, useRef, useState } from 'react';

import { type DragTarget } from './types';
import {
  DragMoveEvent,
  DragStartEvent,
  calculatePosition,
  clamp,
  getClientX,
} from './utils';

// Steven requested to turn off the ability to drag the entire trim bar on the handle when it reaches max gap.
// here you can turn it back on:
const allowDragWholeTrimBarOnHandle = false;

/**
 * Custom hook to handle the drag functionality for a trim bar component.
 *
 * The trim bar has two handles (start and end) and a range between them.
 * This hook manages the dragging logic for these handles and the range,
 * supporting both mouse and touch interactions.
 *
 * @param {number} startPos - The starting position of the trim bar as a percentage (0-100).
 * @param {number} endPos - The ending position of the trim bar as a percentage (0-100).
 * @param {Function} setStartPos - Function to update the start position.
 * @param {Function} setEndPos - Function to update the end position.
 * @param {React.RefObject<HTMLDivElement>} trackRef - Reference to the track element.
 * @param {number} minGapPercent - The minimum gap between the start and end positions as a percentage.
 * @param {number} maxGapPercent - The maximum gap between the start and end positions as a percentage.
 *
 * The `startPos` and `endPos` are represented as percentages of the total width of the track element.
 * This allows for responsive positioning regardless of the actual pixel width of the track.
 */
export const useTrimBarDrag = ({
  startPos,
  endPos,
  setStartPos,
  setEndPos,
  trackRef,
  minGapPercent,
  maxGapPercent,
}: {
  startPos: number;
  endPos: number;
  setStartPos: (pos: number) => void;
  setEndPos: (pos: number) => void;
  trackRef: RefObject<HTMLDivElement | null>;
  minGapPercent: number;
  maxGapPercent: number;
}) => {
  const [isDragging, setIsDragging] = useState<DragTarget>(null);
  const dragOffsetRef = useRef<number>(0);

  const handleDragStart = useCallback(
    (target: DragTarget) => (e: DragStartEvent) => {
      if (!trackRef.current) return;
      const trackRect = trackRef.current.getBoundingClientRect();
      const left = trackRect.left;
      const width = trackRect.width;
      const clientX = getClientX(e);

      if (target === 'range') {
        const rangeStart = calculatePosition.fromPercentage(startPos, 0, width);
        dragOffsetRef.current = clientX - (left + rangeStart);
      } else {
        const handlePos = calculatePosition.fromPercentage(
          target === 'start' ? startPos : endPos,
          0,
          width,
        );
        dragOffsetRef.current = clientX - (left + handlePos);
      }
      setIsDragging(target);
    },
    [startPos, endPos, trackRef],
  );

  const handleDragMove = useCallback(
    (e: DragMoveEvent) => {
      if (!isDragging || !trackRef.current) return;

      const trackRect = trackRef.current.getBoundingClientRect();
      const clientX = getClientX(e);

      const adjustedPos = calculatePosition.fromClientX(
        clientX,
        dragOffsetRef.current,
        trackRect,
      );

      if (isDragging === 'range') {
        const clampedPos = clamp(adjustedPos, 0, 100 - (endPos - startPos));
        setStartPos(clampedPos);
        setEndPos(clampedPos + (endPos - startPos));
      } else {
        const clampedPos = clamp(adjustedPos, 0, 100);

        if (isDragging === 'start') {
          if (clampedPos < endPos - maxGapPercent) {
            if (allowDragWholeTrimBarOnHandle) {
              // If we exceed maxGap, move the end handle to maintain the gap
              setEndPos(clampedPos + maxGapPercent);
              setStartPos(clampedPos);
            }
          } else {
            const maxAllowedPos = endPos - minGapPercent;
            setStartPos(clamp(clampedPos, 0, maxAllowedPos));
          }
        } else {
          // When dragging end handle
          if (clampedPos > startPos + maxGapPercent) {
            if (allowDragWholeTrimBarOnHandle) {
              // If we exceed maxGap, move the start handle to maintain the gap
              setStartPos(clampedPos - maxGapPercent);
              setEndPos(clampedPos);
            }
          } else {
            const minAllowedPos = startPos + minGapPercent;
            setEndPos(clamp(clampedPos, minAllowedPos, 100));
          }
        }
      }
    },
    [isDragging, startPos, endPos, minGapPercent, maxGapPercent, trackRef],
  );

  const handleDragEnd = useCallback(() => {
    setIsDragging(null);
  }, []);

  return {
    isDragging,
    handleDragStart,
    handleDragMove,
    handleDragEnd,
  };
};
